import React, { useCallback, useEffect, useMemo } from "react";

import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "semantic-ui-react";

import LoadingIcon from "../HCU/LoadingIcon";

import {
  RehabHistoryDetailType,
  RehabOrderGroupItemType,
  RehabOrderGroupType,
  RehabOrderType,
} from "./RHBInterface";

import { formatDatetime, getOrdinal } from "react-lib/utils/dateUtils";

import "./TableTreatment.scss";

type TableHistoryProps = {
  setProp: (key: string, value: any, callback?: () => void) => any;
  rehabHistoryDetail?: RehabHistoryDetailType;
  rehabHistoryList?: RehabOrderType[];
  viewRehabHistoryId?: number;
  buttonLoadCheck: any;
  onOpenGallery: (images: any[]) => void;
  onViewDetail: (data: RehabOrderGroupType, btnAction: string) => void;
};

const IMAGES = {
  gallery: "/static/images/rehab/gallery.png",
};

const BUTTON_ACTIONS = {
  select: "SELECT",
};

const TABLE_HISTORY = "TableHistory";
const ACTION_SELECT = `${TABLE_HISTORY}_${BUTTON_ACTIONS.select}`;

const TableHistory = (props: TableHistoryProps) => {
  const groupedList = useMemo(() => {
    const grouped: RehabOrderGroupType[] = [];
    const list = props.rehabHistoryList ?? [];

    for (const order of list) {
      // จัดกลุ่มรายการตามรหัสวินิจฉัย (ICD-10)
      const groupByDiagnosis: Record<string, Record<string, any>[]> = {};

      for (const item of order.items) {
        const secondary: Record<string, any>[] = item.diagnosis?.secondary ?? [];
        const principalCode = item.diagnosis?.principal?.icd10_code || "";
        const secondaryCode = secondary.map((acc) => acc.icd10_code as string);

        // สร้างคีย์เพื่อจัดกลุ่มโดยรวมรหัสโรคหลักและโรครองเป็นสตริงเดียว
        const code = [principalCode, ...secondaryCode].filter(Boolean).join("-");

        groupByDiagnosis[code] ??= [];
        groupByDiagnosis[code].push(item);
      }

      const formattedItems = Object.values(groupByDiagnosis).map((value) => {
        const item: RehabOrderGroupItemType = {
          ...value[0].diagnosis,
          treatments: value,
        };

        return item;
      });

      grouped.push({
        ...order,
        items: formattedItems,
      });
    }

    return grouped;
  }, [props.rehabHistoryList]);

  // Effect
  useEffect(() => {
    // เมื่อมีการกด view มาจากรายการ Performed tab Patient History Viewer
    if (props.viewRehabHistoryId) {
      const findOrder = groupedList.find((order) => order.id === props.viewRehabHistoryId);

      if (findOrder) {
        props.onViewDetail(findOrder, `${ACTION_SELECT}_${findOrder.id}`);
        props.setProp("viewRehabHistoryId", null);
      }
    }
  }, [groupedList, props.viewRehabHistoryId]);

  // Flatten data structure
  const flattenedData = useMemo(() => {
    const result: {
      diagnosisGroup: RehabOrderGroupItemType;
      diagnosisIndex: number;
      order: RehabOrderGroupType;
      orderIndex: number;
      treatment: Record<string, any>;
      treatmentIndex: number;
    }[] = [];

    for (const [orderIndex, order] of groupedList.entries()) {
      for (const [diagnosisIndex, diagnosisGroup] of order.items.entries()) {
        for (const [treatmentIndex, treatment] of diagnosisGroup.treatments.entries()) {
          result.push({
            diagnosisGroup,
            diagnosisIndex,
            order,
            orderIndex,
            treatment,
            treatmentIndex,
          });
        }
      }
    }

    return result;
  }, [groupedList]);

  const renderDiagnosis = useCallback((diagnosisGroup: Record<string, any>) => {
    const secondary: Record<string, any>[] = diagnosisGroup.secondary || [];

    return (
      <div>
        <div>
          {diagnosisGroup.principal && (
            <>
              <div>
                <strong>Principal Dx.</strong>
              </div>
              {`[${diagnosisGroup.principal.icd10_code}] ${diagnosisGroup.principal.icd10_term}`}
            </>
          )}
        </div>
        {secondary[0]?.icd10_code && (
          <>
            <div style={{ marginTop: "0.35rem" }}>
              <strong>Secondary Dx.</strong>
            </div>
            {secondary.map((acc) => (
              <div key={acc.icd10_code}>{`[${acc.icd10_code}] ${acc.icd10_term}`}</div>
            ))}
          </>
        )}
      </div>
    );
  }, []);

  const renderTreatment = useCallback(
    (treatment: Record<string, any>) => {
      const detail = treatment.plan_item_detail ?? {};
      const ordinal = getOrdinal(treatment.no);

      return (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 35px" }}>
          <div>
            <div>
              <strong>
                {ordinal.number}
                <sup>{ordinal.suffix}</sup> {detail.name || ""}
              </strong>
            </div>
            <div>{detail.detail}</div>
          </div>
          <div style={{ display: "flex" }}>
            {(detail.images ?? []).length > 0 && (
              <img
                style={{
                  cursor: "pointer",
                  height: "20px",
                  marginLeft: "0.45rem",
                }}
                alt="gallery.icon"
                src={IMAGES.gallery}
                onClick={() => {
                  props.onOpenGallery(detail.images);
                }}
              />
            )}
          </div>
        </div>
      );
    },
    [props.onOpenGallery]
  );

  const renderPerformed = useCallback(
    (order: RehabOrderGroupType) => (
      <div style={{ lineHeight: 1.75 }}>
        <div>
          <strong>Encounter</strong> : {order.encounter_number}
        </div>
        <div>
          <strong>
            <span style={{ display: "inline-block", width: "40px" }}>Start :</span>
          </strong>{" "}
          {formatDatetime(order.start)}
        </div>
        <div>
          <strong>
            <span style={{ display: "inline-block", width: "40px" }}>End :</span>
          </strong>{" "}
          {formatDatetime(order.finish)}
        </div>
        <div>
          <strong>Performed by</strong> : {order.order_perform_by_fullname}
        </div>
        <div
          style={{ display: "flex", marginTop: "0.5rem" }}
          onClick={() => {
            props.onViewDetail(order, `${ACTION_SELECT}_${order.id}`);
          }}
        >
          <LoadingIcon
            style={{ fontSize: "1.15rem", marginTop: "-3px" }}
            color="blue"
            dimmerStyle={{ marginTop: "1px" }}
            loading={!!props.buttonLoadCheck?.[`${ACTION_SELECT}_${order.id}`]}
            name={props.rehabHistoryDetail?.id === order.id ? "folder open" : "folder outline"}
          />
          <label
            style={{
              color: "rgba(33, 133, 208, 1)",
              cursor: "pointer",
              fontSize: "0.9rem",
              marginLeft: "0.15rem",
            }}
          >
            View Detail
          </label>
        </div>
      </div>
    ),
    [props.buttonLoadCheck, props.onViewDetail, props.rehabHistoryDetail]
  );

  return (
    <Table id="TableHistory" celled compact structured>
      <TableHeader className="rehab-treatment">
        <TableRow style={{ backgroundColor: "#4ecdc4" }}>
          <TableHeaderCell textAlign="center" width={4}>
            Performed
          </TableHeaderCell>
          <TableHeaderCell textAlign="center" width={5}>
            Diagnosis
          </TableHeaderCell>
          <TableHeaderCell textAlign="center" width={7}>
            Treatment order
          </TableHeaderCell>
        </TableRow>
      </TableHeader>

      <TableBody>
        {flattenedData.length === 0 ? (
          <TableCell style={{ color: "grey" }} colSpan="3" textAlign="center">
            No rows found
          </TableCell>
        ) : null}

        {flattenedData.map((row, rowIndex) => {
          // Calculate if this is the first row for order/diagnosis
          const isFirstRowForOrder =
            rowIndex === 0 || flattenedData[rowIndex - 1].orderIndex !== row.orderIndex;

          const isFirstRowForDiagnosis =
            rowIndex === 0 ||
            flattenedData[rowIndex - 1].orderIndex !== row.orderIndex ||
            flattenedData[rowIndex - 1].diagnosisIndex !== row.diagnosisIndex;

          // Calculate rowSpans
          const orderRowSpan = flattenedData.filter((d) => d.orderIndex === row.orderIndex).length;
          const diagnosisRowSpan = flattenedData.filter(
            (d) => d.orderIndex === row.orderIndex && d.diagnosisIndex === row.diagnosisIndex
          ).length;

          return (
            <TableRow key={rowIndex}>
              {/* Performed - merge for all treatments of this order */}
              {isFirstRowForOrder && (
                <TableCell rowSpan={orderRowSpan} verticalAlign="top">
                  {renderPerformed(row.order)}
                </TableCell>
              )}

              {/* Diagnosis - merge for all treatments of this diagnosis group */}
              {isFirstRowForDiagnosis && (
                <TableCell rowSpan={diagnosisRowSpan} verticalAlign="top">
                  {renderDiagnosis(row.diagnosisGroup)}
                </TableCell>
              )}

              {/* Treatment order - individual row for each treatment */}
              <TableCell verticalAlign="top">{renderTreatment(row.treatment)}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

TableHistory.displayName = "TableHistory";

export default React.memo(TableHistory);
